import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// Tracking (writes go through the analytics Edge Function)
// ---------------------------------------------------------------------------

export interface TrackMetadata {
  referrer?: string;
  deviceType?: "mobile" | "tablet" | "desktop";
  screen?: { width: number; height: number };
  timezone?: string;
  visitType?: "new" | "return";
  depth?: number;
  slug?: string;
  provider?: string;
  success?: boolean;
}

function getSecureSessionId(): string {
  const existingId = localStorage.getItem("session_id");
  if (existingId) return existingId;

  const sessionId = crypto.randomUUID();
  localStorage.setItem("session_id", sessionId);
  return sessionId;
}

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent;
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua)) return "tablet";
  if (/Mobile|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

function isReturningVisitor(): boolean {
  if (localStorage.getItem("has_visited")) return true;
  localStorage.setItem("has_visited", "1");
  return false;
}

function baseMetadata(): TrackMetadata {
  return {
    referrer: document.referrer.slice(0, 500),
    deviceType: getDeviceType(),
    screen: { width: window.innerWidth, height: window.innerHeight },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    visitType: isReturningVisitor() ? "return" : "new",
  };
}

async function trackAnalytics(data: {
  event_type: string;
  page_path: string;
  blog_id?: string;
  metadata?: TrackMetadata;
}) {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analytics`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ ...data, session_id: getSecureSessionId() }),
    }
  );

  if (!response.ok) {
    throw new Error(`Analytics failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

export async function trackPageView(path: string, blogId?: string) {
  try {
    return await trackAnalytics({
      event_type: "page_view",
      page_path: path,
      blog_id: blogId,
      metadata: baseMetadata(),
    });
  } catch (error) {
    console.warn("Failed to track page view:", error);
  }
}

export async function trackScrollDepth(path: string, depth: number, blogId?: string) {
  try {
    await trackAnalytics({
      event_type: "scroll_depth",
      page_path: path,
      blog_id: blogId,
      metadata: { depth: Math.round(Math.min(100, Math.max(0, depth))) },
    });
  } catch (error) {
    console.warn("Failed to track scroll depth:", error);
  }
}

/**
 * Auth events intentionally carry no user identifiers (no email / user id /
 * raw user agent) — success + provider is all the dashboard needs.
 */
export async function trackAuthAttempt(success: boolean, provider = "github") {
  try {
    await trackAnalytics({
      event_type: success ? "auth_success" : "auth_attempt",
      page_path: "/auth/login",
      metadata: { success, provider },
    });
  } catch (error) {
    console.warn("Failed to track auth attempt:", error);
  }
}

// ---------------------------------------------------------------------------
// Reporting (admin dashboard; RLS restricts these to admins)
// ---------------------------------------------------------------------------

export interface AnalyticsSummary {
  total_views: number;
  unique_visitors: number;
  sessions: number;
  blog_views: number;
  views_today: number;
  returning_share: number;
}

export interface DailyViews {
  day: string;
  views: number;
  unique_visitors: number;
}

export interface TopPage {
  path: string;
  views: number;
  unique_visitors: number;
}

export interface TopReferrer {
  referrer: string;
  views: number;
}

export interface DeviceBreakdown {
  device: string;
  views: number;
}

export interface BlogEngagement {
  blog_id: string;
  title: string;
  slug: string;
  views: number;
  unique_visitors: number;
  last_viewed: string | null;
}

export interface RecentEvent {
  created_at: string;
  event_type: string;
  page_path: string;
  metadata: {
    unique_visit?: boolean;
    visit_type?: string;
    referrer?: string;
    deviceType?: string;
    browser?: string;
    provider?: string;
  } | null;
}

export async function getAnalyticsSummary(days = 7): Promise<AnalyticsSummary> {
  const { data, error } = await supabase.rpc("get_analytics_summary", {
    days_back: days,
  });
  if (error) throw error;
  return data as AnalyticsSummary;
}

export async function getDailyViews(days = 30): Promise<DailyViews[]> {
  const { data, error } = await supabase.rpc("get_daily_views", {
    days_back: days,
  });
  if (error) throw error;
  return (data ?? []) as DailyViews[];
}

export async function getTopPages(days = 30, maxRows = 10): Promise<TopPage[]> {
  const { data, error } = await supabase.rpc("get_top_pages", {
    days_back: days,
    max_rows: maxRows,
  });
  if (error) throw error;
  return (data ?? []) as TopPage[];
}

export async function getTopReferrers(days = 30, maxRows = 10): Promise<TopReferrer[]> {
  const { data, error } = await supabase.rpc("get_top_referrers", {
    days_back: days,
    max_rows: maxRows,
  });
  if (error) throw error;
  return (data ?? []) as TopReferrer[];
}

export async function getDeviceBreakdown(days = 30): Promise<DeviceBreakdown[]> {
  const { data, error } = await supabase.rpc("get_device_breakdown", {
    days_back: days,
  });
  if (error) throw error;
  return (data ?? []) as DeviceBreakdown[];
}

export async function getBlogEngagement(days = 30): Promise<BlogEngagement[]> {
  const { data, error } = await supabase.rpc("get_blog_engagement", {
    days_back: days,
  });
  if (error) throw error;
  return (data ?? []) as BlogEngagement[];
}

export async function getRecentEvents(limit = 12): Promise<RecentEvent[]> {
  const { data, error } = await supabase
    .from("analytics")
    .select("created_at, event_type, page_path, metadata")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as RecentEvent[];
}
